import { useMemo, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Database,
  Download,
  FileJson,
  Info,
  Search,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { downloadCsv } from '../../lib/csv';
import { timeAgo } from '../../lib/format';
import type { AuditCategory, AuditSeverity, ActorRole } from '../../mocks/audit';
import { useAuditLog } from '../../api/admin';

/**
 * Tolerant server row — the API may return categories/roles outside the mock
 * unions and rows with missing optional fields. Rendering must never crash on
 * a malformed row (the pre-fix page white-screened on `occurredAt.slice`).
 */
type AuditRow = {
  id: string;
  occurredAt?: string | null;
  actor?: string | null;
  actorEmail?: string | null;
  actorRole?: string | null;
  action?: string | null;
  category?: string | null;
  severity?: string | null;
  outcome?: string | null;
  target?: string | null;
  subjectType?: string | null;
  subjectId?: string | null;
  ip?: string | null;
  ipCountry?: string | null;
  userAgent?: string | null;
  requestId?: string | null;
  sessionId?: string | null;
  diff?: Record<string, { before: unknown; after: unknown }> | null;
  metadata?: Record<string, unknown> | null;
};

const CATEGORY_LABEL: Record<AuditCategory, string> = {
  authentication: 'Auth',
  user_management: 'Users',
  business: 'Business',
  subscription: 'Subscription',
  payment: 'Payment',
  configuration: 'Config',
  security: 'Security',
  system: 'System',
  platform: 'Platform',
  reseller: 'Reseller',
  billing: 'Billing',
};

const CATEGORY_TONE: Record<AuditCategory, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  authentication: 'info',
  user_management: 'info',
  business: 'success',
  subscription: 'success',
  payment: 'success',
  configuration: 'neutral',
  security: 'warning',
  system: 'neutral',
  platform: 'info',
  reseller: 'success',
  billing: 'success',
};

const SEVERITY_LABEL: Record<AuditSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
};

const SEVERITY_DOT: Record<AuditSeverity, string> = {
  info: 'bg-sky-500',
  warning: 'bg-amber-500',
  critical: 'bg-rose-600',
};

const SEVERITY_TEXT: Record<AuditSeverity, string> = {
  info: 'text-sky-700 dark:text-sky-300',
  warning: 'text-amber-700 dark:text-amber-300',
  critical: 'text-rose-700 dark:text-rose-300',
};

const ROLE_LABEL: Record<ActorRole, string> = {
  platform_admin: 'Platform admin',
  reseller_admin: 'Reseller admin',
  business_admin: 'Business admin',
  business_staff: 'Business staff',
  system: 'System',
};

const ROLE_BADGE: Record<ActorRole, string> = {
  platform_admin: 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300',
  reseller_admin: 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  business_admin: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
  business_staff: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200',
  system: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400',
};

/** Audit log — comprehensive event timeline. Read-only, tamper-evident. */
export function AuditLog() {
  const { data: auditLogData = [], isLoading } = useAuditLog();
  const auditLog = auditLogData as unknown as AuditRow[];
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<AuditCategory | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<AuditSeverity | 'all'>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<'all' | 'success' | 'failure'>('all');
  const [actorFilter, setActorFilter] = useState<'all' | 'human' | 'system'>('all');
  const [dateFilter, setDateFilter] = useState<'24h' | '7d' | '30d' | 'all'>('30d');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const cutoffMs =
      dateFilter === '24h' ? 24 * 3600_000 : dateFilter === '7d' ? 7 * 86400_000 : dateFilter === '30d' ? 30 * 86400_000 : null;
    const cutoff = cutoffMs === null ? null : Date.now() - cutoffMs;
    return auditLog.filter((e) => {
      if (cutoff !== null) {
        const t = e.occurredAt ? Date.parse(e.occurredAt) : NaN;
        if (Number.isFinite(t) && t < cutoff) return false;
      }
      if (categoryFilter !== 'all' && e.category !== categoryFilter) return false;
      if (severityFilter !== 'all' && e.severity !== severityFilter) return false;
      if (outcomeFilter !== 'all' && e.outcome !== outcomeFilter) return false;
      if (actorFilter === 'human' && e.actorRole === 'system') return false;
      if (actorFilter === 'system' && e.actorRole !== 'system') return false;
      if (search) {
        const s = search.toLowerCase();
        const haystack = [e.actor, e.actorEmail, e.action, e.target, e.ip, e.subjectId, e.requestId]
          .map((v) => v ?? '')
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      return true;
    });
  }, [categoryFilter, severityFilter, outcomeFilter, actorFilter, dateFilter, search, auditLog]);

  const stats = useMemo(() => {
    const total = filtered.length;
    const info = filtered.filter((e) => e.severity === 'info').length;
    const warn = filtered.filter((e) => e.severity === 'warning').length;
    const crit = filtered.filter((e) => e.severity === 'critical').length;
    const failures = filtered.filter((e) => e.outcome === 'failure').length;
    return { total, info, warn, crit, failures };
  }, [filtered]);

  function toggle(id: string) {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(filtered, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportCsv() {
    downloadCsv(
      `audit-log-${new Date().toISOString().slice(0, 10)}`,
      ['timestamp', 'actor', 'role', 'action', 'category', 'severity', 'outcome', 'target', 'subject_id', 'ip', 'country', 'session_id', 'request_id'],
      filtered.map((e) => [
        e.occurredAt, e.actorEmail, e.actorRole, e.action, e.category, e.severity, e.outcome,
        e.target, e.subjectId, e.ip, e.ipCountry, e.sessionId, e.requestId,
      ]),
    );
  }


  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Operations"
        title="Audit log"
        subtitle="Every mutating action across the platform — read-only, tamper-evident, retained 7 years."
        action={
          <>
            <Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />} onClick={exportCsv}>
              <span className="hidden sm:inline">CSV</span>
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<FileJson className="w-4 h-4" />} onClick={exportJson}>
              <span className="hidden sm:inline">JSON</span>
            </Button>
          </>
        }
      />

      {/* Stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
        <StatTile label="Events" value={stats.total} subtitle={`across ${describeRange(dateFilter)}`} icon={<Database className="w-4 h-4" />} tone="neutral" />
        <StatTile label="Info" value={stats.info} subtitle="routine activity" icon={<Info className="w-4 h-4" />} tone="info" />
        <StatTile label="Warning" value={stats.warn} subtitle="needs review" icon={<AlertTriangle className="w-4 h-4" />} tone="warning" />
        <StatTile label="Critical" value={stats.crit} subtitle="immediate attention" icon={<AlertCircle className="w-4 h-4" />} tone="critical" />
        <StatTile label="Failures" value={stats.failures} subtitle="outcome = failure" icon={<XCircle className="w-4 h-4" />} tone="error" />
      </div>

      <Card className="overflow-hidden">
        {/* Filter chip row */}
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Date"
            value={dateFilter === '24h' ? 'Last 24h' : dateFilter === '7d' ? 'Last 7 days' : dateFilter === '30d' ? 'Last 30 days' : 'All time'}
            options={[
              { v: '24h', l: 'Last 24h' },
              { v: '7d', l: 'Last 7 days' },
              { v: '30d', l: 'Last 30 days' },
              { v: 'all', l: 'All time' },
            ]}
            onChange={(v) => setDateFilter(v as typeof dateFilter)}
          />
          <FilterChip
            label="Category"
            value={categoryFilter === 'all' ? 'All' : CATEGORY_LABEL[categoryFilter]}
            options={[
              { v: 'all', l: 'All categories' },
              ...Object.entries(CATEGORY_LABEL).map(([v, l]) => ({ v, l })),
            ]}
            onChange={(v) => setCategoryFilter(v as typeof categoryFilter)}
          />
          <FilterChip
            label="Severity"
            value={severityFilter === 'all' ? 'All' : SEVERITY_LABEL[severityFilter]}
            options={[
              { v: 'all', l: 'All severities' },
              { v: 'info', l: 'Info' },
              { v: 'warning', l: 'Warning' },
              { v: 'critical', l: 'Critical' },
            ]}
            onChange={(v) => setSeverityFilter(v as typeof severityFilter)}
          />
          <FilterChip
            label="Outcome"
            value={outcomeFilter === 'all' ? 'All' : outcomeFilter === 'success' ? 'Success' : 'Failure'}
            options={[
              { v: 'all', l: 'All outcomes' },
              { v: 'success', l: 'Success' },
              { v: 'failure', l: 'Failure' },
            ]}
            onChange={(v) => setOutcomeFilter(v as typeof outcomeFilter)}
          />
          <FilterChip
            label="Actor"
            value={actorFilter === 'all' ? 'All' : actorFilter === 'human' ? 'Humans only' : 'System only'}
            options={[
              { v: 'all', l: 'All actors' },
              { v: 'human', l: 'Humans only' },
              { v: 'system', l: 'System only' },
            ]}
            onChange={(v) => setActorFilter(v as typeof actorFilter)}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search action, target, IP, request id…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Event table */}
        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="px-5 sm:px-6 py-3 w-8"></th>
                <th className="text-left font-medium px-4 py-3">When</th>
                <th className="text-left font-medium px-4 py-3">Actor</th>
                <th className="text-left font-medium px-4 py-3">Action</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Target</th>
                <th className="text-left font-medium px-4 py-3 hidden xl:table-cell">From</th>
                <th className="text-left font-medium px-4 py-3">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3 align-top"><Skeleton className="w-4 h-4 rounded" /></td>
                    <td className="px-4 py-3 align-top">
                      <Skeleton className="h-4 w-20 rounded mb-1.5" />
                      <Skeleton className="h-3 w-16 rounded" />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <Skeleton className="w-7 h-7 rounded-full flex-shrink-0" />
                        <div>
                          <Skeleton className="h-4 w-28 rounded mb-1.5" />
                          <Skeleton className="h-3 w-36 rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-32 rounded" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell align-top">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-24 rounded" />
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell align-top">
                      <Skeleton className="h-4 w-24 rounded mb-1.5" />
                      <Skeleton className="h-3 w-10 rounded" />
                    </td>
                    <td className="px-4 py-3 align-top"><Skeleton className="h-4 w-20 rounded" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No events match your filters.
                  </td>
                </tr>
              ) : (
                filtered.map((e) => (
                  <Row key={e.id} entry={e} expanded={expanded.has(e.id)} onToggle={() => toggle(e.id)} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer / compliance */}
        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 flex items-center justify-between gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
          <span>
            Showing <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-200">{filtered.length}</span> of{' '}
            <span className="tabular-nums">{auditLog.length}</span> events
          </span>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Tamper-evident hash chain · 7-year retention · read-only
          </span>
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

function Row({
  entry: e,
  expanded,
  onToggle,
}: {
  entry: AuditRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={cn(
          'transition cursor-pointer',
          expanded ? 'bg-zinc-50 dark:bg-zinc-900/50' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/70',
        )}
        onClick={onToggle}
      >
        <td className="px-5 sm:px-6 py-3 align-top">
          <ChevronRight className={cn('w-4 h-4 text-zinc-400 transition-transform', expanded && 'rotate-90')} />
        </td>
        <td className="px-4 py-3 align-top">
          <div className="text-sm text-zinc-900 dark:text-zinc-100 tabular-nums">{e.occurredAt ? timeAgo(e.occurredAt) : '—'}</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 tabular-nums mono">{(e.occurredAt ?? '').slice(11, 19)} UTC</div>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 min-w-0">
            <ActorBadge role={e.actorRole} />
            <div className="min-w-0">
              <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{e.actor}</div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate mono">{e.actorEmail}</div>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 align-top">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-xs font-medium text-zinc-900 dark:text-zinc-100 break-all">{e.action ?? '—'}</span>
            <Badge tone={CATEGORY_TONE[e.category as AuditCategory] ?? 'neutral'} dot={false}>
              {CATEGORY_LABEL[e.category as AuditCategory] ?? e.category ?? '—'}
            </Badge>
            {e.outcome === 'failure' && (
              <span className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[10px] font-medium bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900">
                <XCircle className="w-3 h-3" />
                Failed
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell align-top">
          <div className="text-sm text-zinc-700 dark:text-zinc-200 truncate max-w-xs">{e.target}</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 mono truncate">{e.subjectId}</div>
        </td>
        <td className="px-4 py-3 hidden xl:table-cell align-top">
          <div className="text-xs text-zinc-700 dark:text-zinc-200 mono tabular-nums">{e.ip}</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">{e.ipCountry}</div>
        </td>
        <td className="px-4 py-3 align-top">
          <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', SEVERITY_TEXT[e.severity as AuditSeverity] ?? SEVERITY_TEXT.info)}>
            <span className={cn('w-1.5 h-1.5 rounded-full', SEVERITY_DOT[e.severity as AuditSeverity] ?? SEVERITY_DOT.info)} />
            {SEVERITY_LABEL[e.severity as AuditSeverity] ?? e.severity ?? '—'}
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-zinc-50 dark:bg-zinc-900/40">
          <td colSpan={7} className="px-5 sm:px-12 py-4 border-t border-zinc-200 dark:border-zinc-700">
            <ExpandedDetail entry={e} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetail({ entry: e }: { entry: AuditRow }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-xs">
      <Field label="Event ID" value={<span className="mono">{e.id}</span>} />
      <Field label="Request ID" value={<span className="mono">{e.requestId}</span>} />
      <Field label="Session ID" value={<span className="mono">{e.sessionId}</span>} />
      <Field label="When (full)" value={<span className="mono tabular-nums">{e.occurredAt}</span>} />
      <Field label="IP" value={<span className="mono">{[e.ip, e.ipCountry].filter(Boolean).join(' · ') || '—'}</span>} />
      <Field label="User agent" value={<span className="mono break-all text-[11px]">{e.userAgent}</span>} />
      <Field label="Subject" value={<span className="mono">{[e.subjectType, e.subjectId].filter(Boolean).join('/') || '—'}</span>} />
      <Field label="Target" value={e.target} />
      <Field
        label="Outcome"
        value={
          <span className={cn('inline-flex items-center gap-1', e.outcome === 'success' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300')}>
            {e.outcome === 'success' ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {e.outcome}
          </span>
        }
      />

      {e.diff && (
        <div className="sm:col-span-2 lg:col-span-3 mt-2">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-1.5">
            Changes
          </div>
          <div className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 overflow-hidden">
            {Object.entries(e.diff).map(([k, v], i) => (
              <div
                key={k}
                className={cn(
                  'grid grid-cols-1 sm:grid-cols-[180px_1fr_1fr] gap-3 px-3 py-2 text-xs',
                  i > 0 && 'border-t border-zinc-200 dark:border-zinc-700',
                )}
              >
                <span className="font-medium text-zinc-700 dark:text-zinc-200 mono">{k}</span>
                <span className="text-rose-700 dark:text-rose-300 mono break-all">− {JSON.stringify(v.before)}</span>
                <span className="text-emerald-700 dark:text-emerald-300 mono break-all">+ {JSON.stringify(v.after)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {e.metadata && Object.keys(e.metadata).length > 0 && (
        <div className="sm:col-span-2 lg:col-span-3 mt-2">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-1.5">
            Metadata
          </div>
          <pre className="rounded-md border border-zinc-200 dark:border-zinc-700 bg-zinc-950 text-zinc-100 p-3 mono text-[11px] leading-relaxed overflow-x-auto">
{JSON.stringify(e.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 dark:text-zinc-400 mb-0.5">{label}</div>
      <div className="text-sm text-zinc-900 dark:text-zinc-100 break-all">{value}</div>
    </div>
  );
}

function ActorBadge({ role }: { role: string | null | undefined }) {
  if (!role || role === 'system') {
    return (
      <span className="w-7 h-7 grid place-items-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0" title="System">
        <Calendar className="w-3.5 h-3.5" />
      </span>
    );
  }
  const label = ROLE_LABEL[role as ActorRole] ?? role.replace(/_/g, ' ');
  return (
    <span
      className={cn(
        'w-7 h-7 grid place-items-center rounded-full text-[10px] font-semibold flex-shrink-0',
        ROLE_BADGE[role as ActorRole] ?? ROLE_BADGE.business_staff,
      )}
      title={label}
    >
      {label.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase()}
    </span>
  );
}

function StatTile({
  label,
  value,
  subtitle,
  icon,
  tone,
}: {
  label: string;
  value: number;
  subtitle: string;
  icon: React.ReactNode;
  tone: 'info' | 'warning' | 'critical' | 'error' | 'neutral';
}) {
  const palette = {
    info: 'text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40',
    warning: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
    critical: 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40',
    error: 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40',
    neutral: 'text-zinc-700 dark:text-zinc-200 bg-zinc-100 dark:bg-zinc-800',
  }[tone];
  return (
    <Card className="p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className={cn('w-7 h-7 grid place-items-center rounded-md', palette)}>{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-semibold text-zinc-950 dark:text-zinc-50 tabular-nums">{value}</div>
      <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</div>
    </Card>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function describeRange(r: '24h' | '7d' | '30d' | 'all'): string {
  return r === '24h' ? 'last 24h' : r === '7d' ? 'last 7 days' : r === '30d' ? 'last 30 days' : 'all time';
}
